import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Application, RecruitmentJob, updateApplicationStage, rateApplication, rejectApplication } from '../../services/recruitmentService';
import { toast } from 'react-hot-toast';

interface PipelineBoardProps {
  applications: Application[];
  jobs: RecruitmentJob[];
  onRefresh: () => void;
}

const PIPELINE_STAGES = [
  { id: 'applied', label: 'Applied', color: 'bg-blue-100 text-blue-700', icon: '📝' },
  { id: 'screening', label: 'Screening', color: 'bg-purple-100 text-purple-700', icon: '🔍' },
  { id: 'phone_screen', label: 'Phone Screen', color: 'bg-yellow-100 text-yellow-700', icon: '📞' },
  { id: 'interview', label: 'Interview', color: 'bg-orange-100 text-orange-700', icon: '💼' },
  { id: 'final_round', label: 'Final Round', color: 'bg-pink-100 text-pink-700', icon: '⭐' },
  { id: 'offer', label: 'Offer', color: 'bg-green-100 text-green-700', icon: '✉️' },
  { id: 'hired', label: 'Hired', color: 'bg-emerald-100 text-emerald-700', icon: '✅' },
];

const PipelineBoard: React.FC<PipelineBoardProps> = ({ applications, jobs, onRefresh }) => {
  const navigate = useNavigate();
  const [selectedJob, setSelectedJob] = useState<string>('all');
  const [draggedApp, setDraggedApp] = useState<Application | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);

  // Filter applications
  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      if (selectedJob === 'all') return true;
      return app.job_id === selectedJob;
    }).filter(app => app.status === 'active'); // Only show active applications
  }, [applications, selectedJob]);

  // Group by stage
  const applicationsByStage = useMemo(() => {
    const grouped: Record<string, Application[]> = {};
    PIPELINE_STAGES.forEach(stage => {
      grouped[stage.id] = filteredApplications.filter(app => app.current_stage === stage.id);
    });
    return grouped;
  }, [filteredApplications]);

  const handleDragStart = (app: Application) => {
    setDraggedApp(app);
  };

  const handleDragOver = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    setDragOverStage(stageId);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e: React.DragEvent, newStage: string) => {
    e.preventDefault();
    setDragOverStage(null);

    if (!draggedApp || draggedApp.current_stage === newStage) {
      setDraggedApp(null);
      return;
    }

    try {
      await updateApplicationStage(draggedApp.id!, newStage);
      toast.success(`Moved to ${PIPELINE_STAGES.find(s => s.id === newStage)?.label}`);
      onRefresh();
    } catch (error: any) {
      console.error('Failed to update stage:', error);
      toast.error('Failed to update stage');
    } finally {
      setDraggedApp(null);
    }
  };

  const handleRate = async (app: Application, rating: number) => {
    try {
      await rateApplication(app.id!, rating);
      toast.success('Rating updated');
      onRefresh();
    } catch (error: any) {
      console.error('Failed to rate:', error);
      toast.error('Failed to update rating');
    }
  };

  const handleReject = async (app: Application) => {
    if (!window.confirm('Are you sure you want to reject this candidate?')) return;
    
    try {
      await rejectApplication(app.id!);
      toast.success('Candidate rejected');
      onRefresh();
    } catch (error: any) {
      console.error('Failed to reject:', error);
      toast.error('Failed to reject candidate');
    }
  };

  const getCandidateName = (app: any) => {
    if (app.candidate) {
      return `${app.candidate.first_name} ${app.candidate.last_name}`;
    }
    return 'Unknown Candidate';
  };

  const getJobTitle = (app: any) => {
    if (app.job) {
      return app.job.title;
    }
    return 'Unknown Position';
  };

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Recruitment Pipeline</h2>
        <div className="flex items-center gap-4">
          <select
            value={selectedJob}
            onChange={(e) => setSelectedJob(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
          >
            <option value="all">All Jobs ({applications.length})</option>
            {jobs.filter(j => j.status === 'active').map(job => {
              const count = applications.filter(app => app.job_id === job.id).length;
              return (
                <option key={job.id} value={job.id}>
                  {job.title} ({count})
                </option>
              );
            })}
          </select>
          <button
            onClick={onRefresh}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Pipeline Stages */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {PIPELINE_STAGES.map((stage) => {
          const stageApps = applicationsByStage[stage.id] || [];
          const isOver = dragOverStage === stage.id;

          return (
            <div
              key={stage.id}
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, stage.id)}
              className={`flex-shrink-0 w-80 bg-gray-50 rounded-xl p-4 transition-all ${
                isOver ? 'ring-2 ring-purple-500 bg-purple-50' : ''
              }`}
            >
              {/* Stage Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{stage.icon}</span>
                  <h3 className="font-semibold text-gray-900">{stage.label}</h3>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${stage.color}`}>
                  {stageApps.length}
                </span>
              </div>

              {/* Candidate Cards */}
              <div className="space-y-3">
                {stageApps.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    No candidates
                  </div>
                ) : (
                  stageApps.map((app) => (
                    <div
                      key={app.id}
                      draggable
                      onDragStart={() => handleDragStart(app)}
                      onClick={() => setSelectedApp(app)}
                      className={`bg-white rounded-lg p-4 shadow-sm border border-gray-200 cursor-move hover:shadow-md transition-all ${
                        draggedApp?.id === app.id ? 'opacity-50' : ''
                      }`}
                    >
                      {/* Candidate Name */}
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {getCandidateName(app)}
                        </h4>
                        {app.ai_match_score && (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            app.ai_match_score >= 80 ? 'bg-green-100 text-green-700' :
                            app.ai_match_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {app.ai_match_score}%
                          </span>
                        )}
                      </div>

                      {/* Job Title */}
                      <p className="text-xs text-gray-600 mb-3">
                        {getJobTitle(app)}
                      </p>

                      {/* Rating Stars */}
                      <div className="flex items-center gap-1 mb-3">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRate(app, star);
                            }}
                            className={`text-lg transition-colors ${
                              (app.rating || 0) >= star
                                ? 'text-yellow-400'
                                : 'text-gray-300 hover:text-yellow-300'
                            }`}
                          >
                            ★
                          </button>
                        ))}
                      </div>

                      {/* Source & Date */}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>
                          {app.application_source || 'Direct'}
                        </span>
                        <span>
                          {new Date(app.applied_at!).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>

                      {/* Quick Actions */}
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedApp(app);
                          }}
                          className="flex-1 px-2 py-1 text-xs font-medium text-purple-600 hover:bg-purple-50 rounded transition-colors"
                        >
                          View
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReject(app);
                          }}
                          className="flex-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick View Modal */}
      {selectedApp && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedApp(null)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {getCandidateName(selectedApp)}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {getJobTitle(selectedApp)}
                </p>
              </div>
              <button
                onClick={() => setSelectedApp(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Contact Information</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  {(selectedApp as any).candidate?.email && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">✉️</span>
                      <a href={`mailto:${(selectedApp as any).candidate.email}`} className="text-purple-600 hover:underline">
                        {(selectedApp as any).candidate.email}
                      </a>
                    </div>
                  )}
                  {(selectedApp as any).candidate?.phone && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">📞</span>
                      <a href={`tel:${(selectedApp as any).candidate.phone}`} className="text-purple-600 hover:underline">
                        {(selectedApp as any).candidate.phone}
                      </a>
                    </div>
                  )}
                  {(selectedApp as any).candidate?.location && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">📍</span>
                      <span className="text-gray-700">{(selectedApp as any).candidate.location}</span>
                    </div>
                  )}
                </div>
              </section>

              {/* Application Details */}
              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Application Status</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Current Stage</p>
                    <p className="font-medium text-gray-900">
                      {PIPELINE_STAGES.find(s => s.id === selectedApp.current_stage)?.label}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Applied</p>
                    <p className="font-medium text-gray-900">
                      {new Date(selectedApp.applied_at!).toLocaleDateString()}
                    </p>
                  </div>
                  {selectedApp.ai_match_score && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-1">AI Match Score</p>
                      <p className="font-medium text-gray-900">{selectedApp.ai_match_score}%</p>
                    </div>
                  )}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Source</p>
                    <p className="font-medium text-gray-900">{selectedApp.application_source || 'Direct'}</p>
                  </div>
                </div>
              </section>

              {/* AI Highlights */}
              {selectedApp.ai_highlights && selectedApp.ai_highlights.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">✨ AI Highlights</h3>
                  <div className="space-y-2">
                    {selectedApp.ai_highlights.map((highlight, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span className="text-gray-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Cover Letter */}
              {selectedApp.cover_letter && (
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Cover Letter</h3>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedApp.cover_letter}
                  </div>
                </section>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    navigate(`/recruitment/applications/${selectedApp.id}`);
                  }}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                >
                  View Full Profile
                </button>
                <button
                  onClick={() => {
                    handleReject(selectedApp);
                    setSelectedApp(null);
                  }}
                  className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineBoard;
